import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Accordion as AccordionComponent } from "../../basic/Accordion";
import { SliderWithMarks } from "../../../fields/base/Slider";
import FormGroupTile from "../../basic/FormGroupTile";
import {
  AD_TYPES,
  ALGORITHM_OPTIONS,
  BID_STRATEGY_OPTIONS,
  GOALS,
  MAX_BID_DEFAULT,
  MIN_BID_DEFAULT,
  WALMART_SB_MIN_BID_DEFAULT,
  WALMART_SBV_MIN_BID_DEFAULT,
  WALMART_MAX_BID_DEFAULT,
  MAX_ACOS_NEGATION_THESHOLD_DEFAULT,
  CUMULATIVE_NEGATION_THESHOLD_DEFAULT,
  SB_CAMPAIGN_GOAL_OPTIONS,
  SB_CAMPAIGN_GOAL_SUPPORTED_MARKETPLACES,
  WALMART_NEGATIVE_KEYWORD_AD_TYPES,
} from "./constants";
import { NegativeKeywordsField, NegativeAsinsField } from "./fields";
import { Switch } from "../../../fields/base/Switch";
import ConfirmationModal from "../ConfirmationModal";
import { MARKETPLACE_CURRENCY_DENOMINATION } from "../../../../utils/marketplaceConstants";
import checkModule from "../../../../utils/checkModule";
import SponsoredProductCampaignModifiers from "./advancedTargetingOptions/SponsoredProductCampaignModifiers";
import HarvestingConfiguration from "../../../../modules/advertising/components/HarvestingConfiguration";
import AdPlanBiddingRules from "./AdPlanBiddingRules";
import useMerchandisePreferences from "../../../../hooks/useMerchandisePreferences";
import AudienceMultiplierField from "./fields/AudienceMultiplierField";
import { CostControlField } from "./fields/CostControlField";
import PlacementModifierWarning from "./advancedTargetingOptions/PlacementModifierWarning";
import { AlgoDescription } from "./advancedTargetingOptions/AlgoDescriptionTooltip";
import { Checkbox } from "../../../fields/base/Checkbox";
import { css } from "../../../../../styled-system/css";
import { HoverInfo } from "../../basic/Tooltip";
import { useSimpleCan } from "../../../../config/casl/casl";
import { Flex } from "../../../../../styled-system/jsx";
import { AmazonSiteRestrictionsField } from "./advancedTargetingOptions/AmazonSiteRestrictionsField";
import { useResolvedLabels } from "../../../../i18n/useResolvedLabels";

const menuContainer = css({
  margin: "xlarge auto",
  padding: "0 2rem",
});

const subtitleStyle = css({
  fontWeight: 560,
  fontSize: "1.6rem",
  color: "#73706E",
});

const inputContainerStyle = css({
  padding: "0 small small 0",
});

const checkboxContainerStyle = css({
  display: "flex",
  alignItems: "center",
});

const explanationTextStyle = css.raw({
  margin: "small 0",
  color: "#6C757D",
  fontSize: "1.3rem",
  fontWeight: 500,
  maxWidth: "90%",
});

const reviewExplanationTextStyle = css.raw({
  maxWidth: "100%",
});

const AdvancedTargetingOptions = ({
  state,
  errors,
  channel,
  onChange,
  review,
  isModal,
  targetingModalEdit,
  fieldsToValidate,
  marketPlace,
  modules,
  skipToRulesForm,
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation(["advertising"]);

  const { preferences } = useMerchandisePreferences();
  const userCanUpdateAds = useSimpleCan("update", "Ad");

  const showAccordion = (review || state.id) && !targetingModalEdit;

  const nonTrellisCampaignFlag = state.goal === GOALS.IMPORTED_ADS.value;

  let rawAlgorithmOptions;

  if (nonTrellisCampaignFlag) {
    rawAlgorithmOptions = [ALGORITHM_OPTIONS.ROAS_BOOST, ALGORITHM_OPTIONS.GROWTH_MAX, ALGORITHM_OPTIONS.SMART_SPEND];
  } else {
    rawAlgorithmOptions = [
      ALGORITHM_OPTIONS.ROAS_BOOST,
      ALGORITHM_OPTIONS.GROWTH_MAX,
      ALGORITHM_OPTIONS.PRODUCT_LAUNCH,
      ALGORITHM_OPTIONS.SMART_SPEND,
    ];
  }

  if (
    [AD_TYPES.SPONSORED_PRODUCT.value, AD_TYPES.SPONSORED_PRODUCT_AUTO.value].includes(state.adType) &&
    channel === "amazon"
  ) {
    rawAlgorithmOptions.push(ALGORITHM_OPTIONS.PLACEMENT_MAX);
  }

  rawAlgorithmOptions.push(ALGORITHM_OPTIONS.RULES_ENGINE_ONLY);

  const rawCampaignGoalOptions =
    state.newProductCollection || state.storeLandingPage || state.id ?
      [SB_CAMPAIGN_GOAL_OPTIONS.PAGE_VISIT, SB_CAMPAIGN_GOAL_OPTIONS.BRAND_IMPRESSION_SHARE]
    : [SB_CAMPAIGN_GOAL_OPTIONS.PAGE_VISIT];

  const rawBidStrategyOptions = [
    BID_STRATEGY_OPTIONS.UP_DOWN,
    BID_STRATEGY_OPTIONS.FIXED,
    BID_STRATEGY_OPTIONS.DOWN_ONLY,
  ];

  if (nonTrellisCampaignFlag) {
    rawBidStrategyOptions.push(BID_STRATEGY_OPTIONS.RULE_BASED);
  }

  const algorithmOptions = useResolvedLabels(rawAlgorithmOptions, ["advertising"]);
  const campaignGoalOptions = useResolvedLabels(rawCampaignGoalOptions, ["advertising"]);
  const bidStrategyOptions = useResolvedLabels(rawBidStrategyOptions, ["advertising"]);

  const renderAdvancedSettings = () => {
    if ((review && !state.id) || state.canHaveChildren) {
      return null;
    }

    if (nonTrellisCampaignFlag) {
      return (
        <Flex wrap="wrap">
          <Flex wrap="wrap">
            <div style={{ width: "100%" }}>
              <h3 className={subtitleStyle}>{t(($) => $.advertising["Optimization:"])}</h3>
            </div>
            <div
              style={{
                paddingRight: "32px",
                paddingTop: "8px",
              }}
              className={`${css({ width: "100%", lg: { width: "41.67%" } })} ${inputContainerStyle}`}
            >
              <FormGroupTile
                type="select"
                stateName={"algorithm"}
                handleChildFormElement={(key, value) => {
                  let fieldsToReset = {};

                  // If algo changes from placement max to any other, reset the current placement modifiers
                  if (
                    state.algorithm === ALGORITHM_OPTIONS.PLACEMENT_MAX.value &&
                    value.value !== ALGORITHM_OPTIONS.PLACEMENT_MAX.value
                  ) {
                    fieldsToReset = {
                      topOfSearchBidMultiplier: 0,
                      productPageMultiplier: 0,
                      restOfSearchMultiplier: 0,
                    };
                  }

                  if (
                    state.algorithm === ALGORITHM_OPTIONS.SMART_SPEND.value &&
                    value.value !== ALGORITHM_OPTIONS.SMART_SPEND.value
                  ) {
                    fieldsToReset.ignoreSmartSpendACOSRange = false;
                  }

                  if (value.value === ALGORITHM_OPTIONS.ROAS_BOOST.value) {
                    onChange({
                      [key]: value.value,
                      crSelectorEnabled: true,
                      createNewProgram: false,
                      ...fieldsToReset,
                    });
                  } else {
                    onChange({
                      [key]: value.value,
                      crSelectorEnabled: false,
                      createNewProgram: false,
                      ...fieldsToReset,
                    });
                  }
                }}
                defaultValue={algorithmOptions.find((a) => a.value === state.algorithm)}
                options={algorithmOptions}
                disabled={!userCanUpdateAds}
              />
              {state.algorithm === ALGORITHM_OPTIONS.SMART_SPEND.value && (
                <Flex gap={"small"}>
                  <Checkbox
                    label={t(($) => $.advertising["Ignore {metric} range"], {
                      metric: preferences.acos_or_roas.toUpperCase(),
                    })}
                    value={state.ignoreSmartSpendACOSRange}
                    className={css({ marginY: "medium" })}
                    onChange={() => {
                      onChange({
                        ignoreSmartSpendACOSRange: !state.ignoreSmartSpendACOSRange,
                      });
                    }}
                  />
                  <HoverInfo className={css({ display: "inline" })}>
                    {preferences.acos_or_roas === "roas" ?
                      t(
                        ($) =>
                          $.advertising[
                            "When checked, the algorithm will maximize sales with your full budget even if ROAS goes below your target."
                          ],
                      )
                    : t(
                        ($) =>
                          $.advertising[
                            "When checked, the algorithm will maximize sales with your full budget even if ACOS goes above your target."
                          ],
                      )
                    }
                  </HoverInfo>
                </Flex>
              )}
            </div>
            <div className={css({ width: "100%", lg: { width: "58.33%" } })}>
              <p className={css(explanationTextStyle, review && reviewExplanationTextStyle)}>
                <AlgoDescription algorithm={state.algorithm} />
              </p>{" "}
            </div>
            {!(review && isModal) && (
              <AdPlanBiddingRules
                state={state}
                onChange={onChange}
                review={review}
                skipToRulesForm={skipToRulesForm}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                readOnly={!userCanUpdateAds}
              />
            )}
            {state.algorithm === ALGORITHM_OPTIONS.GROWTH_MAX.value && (
              <Flex wrap="wrap" style={{ margin: "3rem auto", width: "100%" }}>
                <div style={{ width: "100%" }}>
                  <h3 className={subtitleStyle}>{t(($) => $.advertising["Sales Volume Control:"])}</h3>
                </div>
                <div style={{ margin: "auto" }} className={css({ width: "100%", lg: { width: "83.33%" } })}>
                  <SliderWithMarks
                    aria-label={t(($) => $.advertising["Sales volume control"])}
                    step={1}
                    minValue={0}
                    maxValue={9}
                    marks={[
                      { value: 0, label: t(($) => $.advertising["Increase Margin"]) },
                      { value: 1, label: t(($) => $.advertising["Optimal Profit"]) },
                      { value: 2 },
                      { value: 3 },
                      { value: 4 },
                      { value: 5 },
                      { value: 6 },
                      { value: 7 },
                      { value: 8 },
                      { value: 9, label: t(($) => $.advertising["Max Volume"]) },
                    ]}
                    value={state.salesVolumeControl}
                    onChange={(value) => {
                      onChange({
                        salesVolumeControl: value,
                      });
                    }}
                    isDisabled={!userCanUpdateAds}
                  />
                </div>
              </Flex>
            )}
          </Flex>
          <Flex
            wrap="wrap"
            style={{
              marginTop: "8px",
              marginBottom: "0",
            }}
          >
            <div style={{ width: "100%" }}>
              <h3 className={subtitleStyle}>{t(($) => $.advertising["Bid Range:"])}</h3>
            </div>
            <Flex
              wrap="wrap"
              style={{
                paddingTop: "8px",
              }}
              className={`${css({ width: "100%", lg: { width: "41.67%" } })} ${inputContainerStyle}`}
            >
              <div style={{ padding: "0 2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                <label
                  style={{
                    color: "#73706E",
                    fontWeight: "600",
                    paddingBottom: ".7rem",
                  }}
                >
                  {t(($) => $.advertising["Min Bid"])}
                </label>
                <FormGroupTile
                  type="formcontrol"
                  prefix={MARKETPLACE_CURRENCY_DENOMINATION[marketPlace.marketPlace]}
                  prefixSide={"left"}
                  formControlType={"number"}
                  stateName={"minBid"}
                  handleChildFormElement={(key, value) => {
                    if (!value || !parseFloat(value)) {
                      onChange({
                        [key]:
                          [AD_TYPES.SPONSORED_BRAND_WALMART.value].includes(state.adType) ? WALMART_SB_MIN_BID_DEFAULT
                          : [AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(state.adType) ?
                            WALMART_SBV_MIN_BID_DEFAULT
                          : MIN_BID_DEFAULT,
                      });
                      return;
                    }
                    onChange({ [key]: value });
                  }}
                  defaultValue={
                    (
                      state.minBid === MIN_BID_DEFAULT ||
                      ([AD_TYPES.SPONSORED_BRAND_WALMART.value].includes(state.adType) &&
                        state.minBid === WALMART_SB_MIN_BID_DEFAULT) ||
                      ([AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(state.adType) &&
                        state.minBid === WALMART_SBV_MIN_BID_DEFAULT) ||
                      (![AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value, AD_TYPES.SPONSORED_BRAND_WALMART.value].includes(
                        state.adType,
                      ) &&
                        state.minBid === MIN_BID_DEFAULT)
                    ) ?
                      null
                    : state.minBid
                  }
                  disabled={!userCanUpdateAds}
                />
              </div>

              <div style={{ padding: "0 2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                <label
                  style={{
                    color: "#73706E",
                    fontWeight: "600",
                    paddingBottom: ".7rem",
                  }}
                >
                  {t(($) => $.advertising["Max Bid"])}
                </label>
                <FormGroupTile
                  type="formcontrol"
                  prefix={MARKETPLACE_CURRENCY_DENOMINATION[marketPlace.marketPlace]}
                  prefixSide={"left"}
                  formControlType={"number"}
                  stateName={"maxBid"}
                  handleChildFormElement={(key, value) => {
                    if (!value || !parseFloat(value)) {
                      onChange({
                        [key]: channel === "walmart" ? WALMART_MAX_BID_DEFAULT : MAX_BID_DEFAULT,
                      });
                      return;
                    }

                    onChange({ [key]: value });
                  }}
                  defaultValue={
                    (
                      state.maxBid === MAX_BID_DEFAULT ||
                      (channel === "walmart" && state.maxBid === WALMART_MAX_BID_DEFAULT)
                    ) ?
                      null
                    : state.maxBid
                  }
                  disabled={!userCanUpdateAds}
                />
              </div>
              <div style={{ padding: "2rem 2rem 1rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                <label
                  style={{
                    color: "#73706E",
                    fontWeight: "600",
                    paddingBottom: ".7rem",
                    display: "flex",
                  }}
                >
                  {t(($) => $.advertising["Pause Bid Optimization"])}
                </label>
                <Switch
                  isSelected={state.disableBidding}
                  onChange={() => {
                    onChange({
                      disableBidding: !state.disableBidding,
                    });
                  }}
                  isDisabled={!userCanUpdateAds}
                />
              </div>
            </Flex>
            <div className={css({ width: "100%", lg: { width: "58.33%" } })}>
              <p
                className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
                style={{ paddingTop: "2rem" }}
              >
                {t(($) => $.advertising["Set the minimum and maximum values for bid keywords and product targets."])}
              </p>
            </div>
          </Flex>
          {[AD_TYPES.SPONSORED_PRODUCT.value, AD_TYPES.SPONSORED_PRODUCT_AUTO.value].includes(state.adType) && (
            <SponsoredProductCampaignModifiers
              state={state}
              onChange={onChange}
              errors={errors}
              fieldsToValidate={fieldsToValidate}
              review={review}
              bidStrategyOptions={bidStrategyOptions}
              readOnly={!userCanUpdateAds}
            />
          )}
          {([AD_TYPES.SPONSORED_PRODUCT_AUTO.value]?.includes(state.adType) ||
            ([AD_TYPES.SPONSORED_PRODUCT.value].includes(state.adType) && state.campaignNames?.auto_name)) && (
            <Flex
              wrap="wrap"
              style={{
                marginTop: "8px",
                marginBottom: "0",
              }}
            >
              <div style={{ width: "100%" }}>
                <h3 className={subtitleStyle}>{t(($) => $.advertising["Automatic Keyword Negation Settings:"])}</h3>
              </div>
              <Flex
                wrap="wrap"
                style={{
                  paddingTop: "8px",
                }}
                className={`${css({ width: "100%", lg: { width: "41.67%" } })} ${inputContainerStyle}`}
              >
                <div
                  style={{ padding: "2rem .5rem 0" }}
                  className={`${css({ width: "100%", "2xl": { width: "75%" } })} d-flex align-items-center`}
                >
                  <Checkbox
                    label={t(($) => $.advertising["Auto Negation"])}
                    value={state.autoNegationEnabled}
                    onChange={(value) => {
                      if (value) {
                        onChange({
                          maxAcosNegationThreshold: MAX_ACOS_NEGATION_THESHOLD_DEFAULT,
                          cumulativeNegationThreshold: CUMULATIVE_NEGATION_THESHOLD_DEFAULT,
                          autoNegationEnabled: true,
                        });
                      } else {
                        onChange({
                          maxAcosNegationThreshold: null,
                          cumulativeNegationThreshold: null,
                          autoNegationEnabled: false,
                        });
                      }
                    }}
                    isDisabled={!userCanUpdateAds}
                  />
                </div>
                {state.autoNegationEnabled && (
                  <>
                    <div style={{ padding: "2rem 2rem 0" }}>
                      <label
                        style={{
                          color: "#73706E",
                          fontWeight: "600",
                          paddingBottom: ".7rem",
                        }}
                      >
                        {t(($) => $.advertising["Optimal ACOS Range High Negation Threshold"])}
                      </label>
                      <div style={{ paddingRight: "2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                        <FormGroupTile
                          type="formcontrol"
                          prefix={"%"}
                          prefixSide={"right"}
                          formControlType={"number"}
                          stateName={"maxAcosNegationThreshold"}
                          handleChildFormElement={(key, value) => {
                            if (value < 0) {
                              return;
                            }

                            onChange({
                              [key]: value,
                              maxAcosNegationThreshold: parseInt(value) ?? 0,
                            });
                          }}
                          placeholder={t(($) => $.advertising["Negation threshold"])}
                          defaultValue={parseInt(state.maxAcosNegationThreshold)}
                          disabled={!userCanUpdateAds}
                        />
                      </div>
                    </div>

                    <div style={{ padding: "2rem 2rem 0" }}>
                      <label
                        style={{
                          color: "#73706E",
                          fontWeight: "600",
                          paddingBottom: ".7rem",
                        }}
                      >
                        {t(($) => $.advertising["Cumulative Negation Threshold"])}
                      </label>

                      <div style={{ paddingRight: "2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                        <FormGroupTile
                          type="formcontrol"
                          prefix={"%"}
                          prefixSide={"right"}
                          formControlType={"number"}
                          stateName={"cumulativeNegationThreshold"}
                          handleChildFormElement={(key, value) => {
                            if (value < 0) {
                              return;
                            }

                            onChange({
                              [key]: value,
                              cumulativeNegationThreshold: parseInt(value) ?? 0,
                            });
                          }}
                          placeholder={t(($) => $.advertising["Negation threshold"])}
                          defaultValue={parseInt(state.cumulativeNegationThreshold)}
                          disabled={!userCanUpdateAds}
                        />
                      </div>
                    </div>
                  </>
                )}
              </Flex>
              <div style={{ paddingRight: "5rem" }} className={css({ width: "100%", lg: { width: "58.33%" } })}>
                {state.autoNegationEnabled && (
                  <>
                    <p
                      className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
                      style={{ paddingTop: "85px" }}
                    >
                      {t(
                        ($) =>
                          $.advertising[
                            "Keywords above {percent}% ACOS will be negated after they accumulate sufficient spend."
                          ],
                        {
                          percent:
                            (state.maxAcosNegationThreshold / 100) *
                            (preferences.acos_or_roas === "roas" ?
                              (1 / parseFloat(state.maxACOS)) * 100
                            : state.maxACOS),
                        },
                      )}
                    </p>
                    <p
                      className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
                      style={{ paddingTop: "50px" }}
                    >
                      {t(
                        ($) => $.advertising["If the media plan ACOS is below {percent}% auto negation will not run."],
                        {
                          percent:
                            (state.cumulativeNegationThreshold / 100) *
                            (preferences.acos_or_roas === "roas" ?
                              (1 / parseFloat(state.maxACOS)) * 100
                            : state.maxACOS),
                        },
                      )}
                    </p>
                  </>
                )}
              </div>
            </Flex>
          )}
        </Flex>
      );
    }

    return (
      <Flex wrap="wrap">
        {![
          AD_TYPES.SPONSORED_PRODUCT_AUTO.value,
          AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value,
          AD_TYPES.SPONSORED_BRAND_WALMART.value,
          AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value,
        ].includes(state.adType) &&
          !(channel === "walmart" && state.goal === GOALS.SOV.value) && (
            <Flex wrap="wrap">
              <div style={{ width: "100%" }}>
                <h3 className={subtitleStyle}>{t(($) => $.advertising["Discovery and Harvesting:"])}</h3>
              </div>
              <div className={css({ width: "100%", lg: { width: "41.67%" } })}>
                {((!state.hasParent && state.adType === AD_TYPES.SPONSORED_PRODUCT.value) || channel === "walmart") &&
                  state.goal !== GOALS.SOV.value && (
                    <div className={checkboxContainerStyle}>
                      {state.id && state.autoCampaignEnabled ?
                        <ConfirmationModal
                          title={t(($) => $.advertising["Confirm Auto Campaign Update"])}
                          confirmButtonText={t(($) => $.advertising["Confirm"])}
                          confirmationMessage={
                            <p className="fs-3 w-75 mx-auto">
                              {t(
                                ($) =>
                                  $.advertising[
                                    "You are disabling the primary source of harvesting for your media plan, are you sure you want to continue? This change will not go live until you update the media plan."
                                  ],
                              )}
                            </p>
                          }
                          onConfirm={() => {
                            onChange({
                              autoCampaignEnabled: !state.autoCampaignEnabled,
                            });
                          }}
                          modelButtonStyle={{ margin: 0 }}
                          inlineModal={true}
                          modalButtonText={
                            <Checkbox
                              label={t(($) => $.advertising["Auto Campaign"])}
                              value={state.autoCampaignEnabled}
                              isDisabled={!userCanUpdateAds}
                            />
                          }
                        />
                      : <Checkbox
                          label={t(($) => $.advertising["Auto Campaign"])}
                          value={state.autoCampaignEnabled}
                          isDisabled={
                            (checkModule(modules, "CAMPAIGN_NAMES") && state.campaignNames.auto_name) ||
                            !userCanUpdateAds
                          }
                          onChange={(value) => {
                            const updatedCampaignNames = state.campaignNames;
                            if (value) {
                              if (!("auto_name" in state.campaignNames)) {
                                updatedCampaignNames["auto_name"] = null;
                              }
                            } else if ("auto_name" in state.campaignNames) {
                              delete updatedCampaignNames["auto_name"];
                            }
                            onChange({
                              autoCampaignEnabled: !state.autoCampaignEnabled,
                              campaignNames: updatedCampaignNames,
                            });
                          }}
                        />
                      }
                    </div>
                  )}

                {[
                  AD_TYPES.SPONSORED_PRODUCT.value,
                  AD_TYPES.SPONSORED_BRAND.value,
                  AD_TYPES.SPONSORED_BRAND_V2.value,
                  AD_TYPES.SPONSORED_BRAND_VIDEO.value,
                  AD_TYPES.SPONSORED_DISPLAY.value,
                  AD_TYPES.SPONSORED_DISPLAY_V2.value,
                  AD_TYPES.SPONSORED_PRODUCT_WALMART.value,
                ].includes(state.adType) && (
                  <div className={checkboxContainerStyle}>
                    <Checkbox
                      label={t(($) => $.advertising["Enable Keyword Harvesting"])}
                      value={state.keywordHarvestingEnabled}
                      onChange={() => {
                        onChange({
                          keywordHarvestingEnabled: !state.keywordHarvestingEnabled,
                        });
                      }}
                      isDisabled={!userCanUpdateAds}
                    />
                  </div>
                )}
                {[
                  AD_TYPES.SPONSORED_PRODUCT.value,
                  AD_TYPES.SPONSORED_BRAND.value,
                  AD_TYPES.SPONSORED_BRAND_V2.value,
                  AD_TYPES.SPONSORED_BRAND_VIDEO.value,
                  AD_TYPES.SPONSORED_DISPLAY.value,
                  AD_TYPES.SPONSORED_DISPLAY_V2.value,
                ].includes(state.adType) &&
                  channel !== "walmart" && (
                    <div className={checkboxContainerStyle}>
                      <Checkbox
                        label={t(($) => $.advertising["Enable ASIN Harvesting"])}
                        value={state.asinHarvestingEnabled}
                        onChange={() => {
                          onChange({
                            asinHarvestingEnabled: !state.asinHarvestingEnabled,
                          });
                        }}
                        isDisabled={!userCanUpdateAds}
                      />
                    </div>
                  )}
                {!state.hasParent && channel !== "walmart" && (
                  <div className={checkboxContainerStyle}>
                    <Checkbox
                      label={t(($) => $.advertising["Bootstrap with historical targets"])}
                      value={state.trellisTrakEnabled}
                      onChange={(v) => {
                        onChange({
                          trellisTrakEnabled: v,
                        });
                      }}
                      isDisabled={!userCanUpdateAds}
                    />
                  </div>
                )}
              </div>
              <div className={css({ width: "100%", lg: { width: "58.33%" } })}>
                <p className={css(explanationTextStyle, review && reviewExplanationTextStyle)}>
                  {t(($) => $.advertising["Enable and disable automated keyword discovery and harvesting."])}
                </p>
                {checkModule(modules, "CAMPAIGN_NAMES") && state.campaignNames.auto_name ?
                  <p className={css(explanationTextStyle, review && reviewExplanationTextStyle)}>
                    {t(
                      ($) =>
                        $.advertising[
                          "Since a custom name was provided for the auto campaign, this field is disabled. If you wish to have no auto campaign for this ad, please remove the custom name."
                        ],
                    )}
                  </p>
                : null}
              </div>
            </Flex>
          )}
        <Flex wrap="wrap">
          <Flex wrap="wrap">
            <div style={{ width: "100%" }}>
              <h3 className={subtitleStyle}>{t(($) => $.advertising["Optimization:"])}</h3>
            </div>
            <div
              style={{
                paddingRight: "32px",
                paddingTop: "8px",
              }}
              className={`${css({ width: "100%", lg: { width: "41.67%" } })} ${inputContainerStyle}`}
            >
              <FormGroupTile
                type="select"
                stateName={"algorithm"}
                handleChildFormElement={(key, value) => {
                  let fieldsToReset = {};

                  // If algo changes from placement max to any other, reset the current placement modifiers
                  if (
                    state.algorithm === ALGORITHM_OPTIONS.PLACEMENT_MAX.value &&
                    value.value !== ALGORITHM_OPTIONS.PLACEMENT_MAX.value
                  ) {
                    fieldsToReset = {
                      topOfSearchBidMultiplier: 0,
                      productPageMultiplier: 0,
                      restOfSearchMultiplier: 0,
                    };
                  }

                  if (
                    state.algorithm === ALGORITHM_OPTIONS.SMART_SPEND.value &&
                    value.value !== ALGORITHM_OPTIONS.SMART_SPEND.value
                  ) {
                    fieldsToReset.ignoreSmartSpendACOSRange = false;
                  }

                  if (value.value === ALGORITHM_OPTIONS.ROAS_BOOST.value) {
                    onChange({
                      [key]: value.value,
                      crSelectorEnabled: true,
                      createNewProgram: false,
                      ...fieldsToReset,
                    });
                  } else {
                    onChange({
                      [key]: value.value,
                      crSelectorEnabled: false,
                      createNewProgram: false,
                      ...fieldsToReset,
                    });
                  }
                }}
                disabled={!userCanUpdateAds}
                defaultValue={algorithmOptions.find((a) => a.value === state.algorithm)}
                options={algorithmOptions}
              />
              {state.algorithm === ALGORITHM_OPTIONS.SMART_SPEND.value && (
                <Flex gap={"small"}>
                  <Checkbox
                    label={t(($) => $.advertising["Ignore {metric} range"], {
                      metric: preferences.acos_or_roas.toUpperCase(),
                    })}
                    value={state.ignoreSmartSpendACOSRange}
                    className={css({ marginY: "medium" })}
                    onChange={() => {
                      onChange({
                        ignoreSmartSpendACOSRange: !state.ignoreSmartSpendACOSRange,
                      });
                    }}
                  />
                  <HoverInfo className={css({ display: "inline" })}>
                    {preferences.acos_or_roas === "roas" ?
                      t(
                        ($) =>
                          $.advertising[
                            "When checked, the algorithm will maximize sales with your full budget even if ROAS goes below your target."
                          ],
                      )
                    : t(
                        ($) =>
                          $.advertising[
                            "When checked, the algorithm will maximize sales with your full budget even if ACOS goes above your target."
                          ],
                      )
                    }
                  </HoverInfo>
                </Flex>
              )}
            </div>
            <div className={css({ width: "100%", lg: { width: "58.33%" } })}>
              <p className={css(explanationTextStyle, review && reviewExplanationTextStyle)}>
                <AlgoDescription algorithm={state.algorithm} />
              </p>{" "}
            </div>
            {!(review && isModal) && (
              <AdPlanBiddingRules
                state={state}
                onChange={onChange}
                review={review}
                skipToRulesForm={skipToRulesForm}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                readOnly={!userCanUpdateAds}
              />
            )}

            {state.algorithm === ALGORITHM_OPTIONS.GROWTH_MAX.value && (
              <Flex wrap="wrap" style={{ margin: "3rem auto", width: "100%" }}>
                <div style={{ width: "100%" }}>
                  <h3 className={subtitleStyle}>{t(($) => $.advertising["Sales Volume Control:"])}</h3>
                </div>
                <div style={{ margin: "auto" }} className={css({ width: "100%", lg: { width: "83.33%" } })}>
                  <SliderWithMarks
                    aria-label={t(($) => $.advertising["Sales volume control"])}
                    step={1}
                    minValue={0}
                    maxValue={9}
                    marks={[
                      { value: 0, label: t(($) => $.advertising["Increase Margin"]) },
                      { value: 1, label: t(($) => $.advertising["Optimal Profit"]) },
                      { value: 2 },
                      { value: 3 },
                      { value: 4 },
                      { value: 5 },
                      { value: 6 },
                      { value: 7 },
                      { value: 8 },
                      { value: 9, label: t(($) => $.advertising["Max Volume"]) },
                    ]}
                    value={state.salesVolumeControl}
                    onChange={(value) => {
                      onChange({
                        salesVolumeControl: value,
                      });
                    }}
                    isDisabled={!userCanUpdateAds}
                  />
                </div>
              </Flex>
            )}
          </Flex>
          {[
            AD_TYPES.SPONSORED_PRODUCT.value,
            AD_TYPES.SPONSORED_PRODUCT_WALMART.value,
            AD_TYPES.SPONSORED_BRAND.value,
            AD_TYPES.SPONSORED_BRAND_V2.value,
            AD_TYPES.SPONSORED_BRAND_VIDEO.value,
          ].includes(state.adType) && (
            <Flex wrap="wrap">
              <div style={{ width: "100%" }}>
                <h3 className={subtitleStyle}>{t(($) => $.advertising["Match Type:"])}</h3>
              </div>
              <Flex wrap="wrap" className={`${css({ width: "100%", lg: { width: "41.67%" } })} ${inputContainerStyle}`}>
                <div className={`${css({ width: "100%", "2xl": { width: "33.33%" } })} ${checkboxContainerStyle}`}>
                  <Checkbox
                    label={t(($) => $.advertising["Broad"])}
                    value={state.broadMatchEnabled}
                    isDisabled={state.initialBroadMatchEnabled || !userCanUpdateAds}
                    onChange={() => {
                      onChange({
                        broadMatchEnabled: !state.broadMatchEnabled,
                      });
                    }}
                  />
                </div>
                <div className={`${css({ width: "100%", "2xl": { width: "33.33%" } })} ${checkboxContainerStyle}`}>
                  <Checkbox
                    label={t(($) => $.advertising["Phrase"])}
                    value={state.phraseMatchEnabled}
                    isDisabled={state.initialPhraseMatchEnabled || !userCanUpdateAds}
                    onChange={() => {
                      onChange({
                        phraseMatchEnabled: !state.phraseMatchEnabled,
                      });
                    }}
                  />
                </div>
                <div className={`${css({ width: "100%", "2xl": { width: "33.33%" } })} ${checkboxContainerStyle}`}>
                  <Checkbox
                    label={t(($) => $.advertising["Exact"])}
                    value={state.exactMatchEnabled}
                    isDisabled={state.initialExactMatchEnabled || !userCanUpdateAds}
                    onChange={() => {
                      onChange({
                        exactMatchEnabled: !state.exactMatchEnabled,
                      });
                    }}
                  />
                </div>
              </Flex>
              <div className={css({ width: "100%", lg: { width: "58.33%" } })}>
                <p
                  className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
                  style={{ paddingTop: "2rem" }}
                >
                  {t(
                    ($) =>
                      $.advertising[
                        "Choose how closely your keywords should match with customer searches by selecting keyword match types. Keyword match types that are not enabled will not be targeted."
                      ],
                  )}
                </p>
              </div>
            </Flex>
          )}
          {[
            AD_TYPES.SPONSORED_PRODUCT.value,
            AD_TYPES.SPONSORED_BRAND.value,
            AD_TYPES.SPONSORED_BRAND_V2.value,
            AD_TYPES.SPONSORED_BRAND_VIDEO.value,
          ].includes(state.adType) &&
            state.broadMatchEnabled &&
            channel === "amazon" && (
              <Flex wrap="wrap">
                <div style={{ width: "100%" }}>
                  <h3 className={subtitleStyle}>{t(($) => $.advertising["Broad Modifier:"])}</h3>
                </div>
                <Flex
                  wrap="wrap"
                  className={`${css({ width: "100%", lg: { width: "41.67%" } })} ${inputContainerStyle}`}
                >
                  <div className={`${css({ width: "100%", "2xl": { width: "50%" } })} ${checkboxContainerStyle} ps-3`}>
                    <Checkbox
                      label={t(($) => $.advertising["Broad Match with Modifier"])}
                      value={state.broadMatchModifier}
                      isDisabled={state.initialBroadMatchModifier || !userCanUpdateAds}
                      onChange={() => {
                        // Make sure one option always selected
                        if (!state.broadMatchRegular && state.broadMatchModifier) {
                          onChange({
                            broadMatchModifier: false,
                            broadMatchRegular: true,
                          });
                        } else {
                          onChange({
                            broadMatchModifier: !state.broadMatchModifier,
                          });
                        }
                      }}
                    />
                  </div>
                  <div className={`${css({ width: "100%", "2xl": { width: "50%" } })} ${checkboxContainerStyle} ps-3`}>
                    <Checkbox
                      label={t(($) => $.advertising["Broad Match"])}
                      value={state.broadMatchRegular}
                      isDisabled={state.initialBroadMatchRegular || !userCanUpdateAds}
                      onChange={() => {
                        // Make sure one option always selected
                        if (!state.broadMatchModifier && state.broadMatchRegular) {
                          onChange({
                            broadMatchRegular: false,
                            broadMatchModifier: true,
                          });
                        } else {
                          onChange({
                            broadMatchRegular: !state.broadMatchRegular,
                          });
                        }
                      }}
                    />
                  </div>
                </Flex>
                <div className={css({ width: "100%", lg: { width: "58.33%" } })}>
                  <p
                    className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
                    style={{ paddingTop: "2rem" }}
                  >
                    {t(
                      ($) => $.advertising["Choose how broadly keyword matches can be made to reach a wider audience."],
                    )}
                  </p>
                </div>
              </Flex>
            )}
        </Flex>
        <Flex
          wrap="wrap"
          style={{
            marginTop: "8px",
            marginBottom: "0",
          }}
        >
          <div style={{ width: "100%" }}>
            <h3 className={subtitleStyle}>{t(($) => $.advertising["Bid Range:"])}</h3>
          </div>
          <Flex
            wrap="wrap"
            style={{
              paddingTop: "8px",
            }}
            className={`${css({ width: "100%", lg: { width: "41.67%" } })} ${inputContainerStyle}`}
          >
            <div style={{ padding: "0 2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
              <label
                style={{
                  color: "#73706E",
                  fontWeight: "600",
                  paddingBottom: ".7rem",
                }}
              >
                {t(($) => $.advertising["Min Bid"])}
              </label>
              <FormGroupTile
                type="formcontrol"
                prefix={MARKETPLACE_CURRENCY_DENOMINATION[marketPlace.marketPlace]}
                prefixSide={"left"}
                formControlType={"number"}
                stateName={"minBid"}
                handleChildFormElement={(key, value) => {
                  if (!value || !parseFloat(value)) {
                    onChange({
                      [key]:
                        [AD_TYPES.SPONSORED_BRAND_WALMART.value].includes(state.adType) ? WALMART_SB_MIN_BID_DEFAULT
                        : [AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(state.adType) ?
                          WALMART_SBV_MIN_BID_DEFAULT
                        : MIN_BID_DEFAULT,
                    });
                    return;
                  }
                  onChange({ [key]: value });
                }}
                defaultValue={
                  (
                    state.minBid === MIN_BID_DEFAULT ||
                    ([AD_TYPES.SPONSORED_BRAND_WALMART.value].includes(state.adType) &&
                      state.minBid === WALMART_SB_MIN_BID_DEFAULT) ||
                    ([AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(state.adType) &&
                      state.minBid === WALMART_SBV_MIN_BID_DEFAULT) ||
                    (![AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value, AD_TYPES.SPONSORED_BRAND_WALMART.value].includes(
                      state.adType,
                    ) &&
                      state.minBid === MIN_BID_DEFAULT)
                  ) ?
                    null
                  : state.minBid
                }
                disabled={!userCanUpdateAds}
              />
            </div>
            <div style={{ padding: "0 2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
              <label
                style={{
                  color: "#73706E",
                  fontWeight: "600",
                  paddingBottom: ".7rem",
                }}
              >
                {t(($) => $.advertising["Max Bid"])}
              </label>
              <FormGroupTile
                type="formcontrol"
                prefix={MARKETPLACE_CURRENCY_DENOMINATION[marketPlace.marketPlace]}
                prefixSide={"left"}
                formControlType={"number"}
                stateName={"maxBid"}
                handleChildFormElement={(key, value) => {
                  if (!value || !parseFloat(value)) {
                    onChange({
                      [key]: channel === "walmart" ? WALMART_MAX_BID_DEFAULT : MAX_BID_DEFAULT,
                    });
                    return;
                  }

                  onChange({ [key]: value });
                }}
                defaultValue={
                  (
                    state.maxBid === MAX_BID_DEFAULT ||
                    (channel === "walmart" && state.maxBid === WALMART_MAX_BID_DEFAULT)
                  ) ?
                    null
                  : state.maxBid
                }
                disabled={!userCanUpdateAds}
              />
            </div>
            <div style={{ padding: "2rem 2rem 1rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
              <label
                style={{
                  color: "#73706E",
                  fontWeight: "600",
                  paddingBottom: ".7rem",
                  display: "flex",
                }}
              >
                {t(($) => $.advertising["Pause Bid Optimization"])}
              </label>
              <Switch
                className={css({ boxSizing: "border-box" })}
                isSelected={state.disableBidding}
                onChange={() => {
                  onChange({
                    disableBidding: !state.disableBidding,
                  });
                }}
                isDisabled={!userCanUpdateAds}
              />
            </div>
          </Flex>

          <div className={css({ width: "100%", lg: { width: "58.33%" } })}>
            <p
              className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
              style={{ paddingTop: "2rem" }}
            >
              {t(($) => $.advertising["Set the minimum and maximum values for bid keywords and product targets."])}
            </p>
          </div>
        </Flex>
        {channel === "amazon" &&
          [
            AD_TYPES.SPONSORED_PRODUCT.value,
            AD_TYPES.SPONSORED_PRODUCT_AUTO.value,
            AD_TYPES.SPONSORED_BRAND_VIDEO.value,
            AD_TYPES.SPONSORED_BRAND.value,
            AD_TYPES.SPONSORED_BRAND_V2.value,
          ].includes(state.adType) && (
            <AmazonSiteRestrictionsField
              state={state}
              onChange={onChange}
              review={review}
              readOnly={!userCanUpdateAds || state.id}
            />
          )}
        {[AD_TYPES.SPONSORED_PRODUCT.value, AD_TYPES.SPONSORED_PRODUCT_AUTO.value].includes(state.adType) &&
          channel === "amazon" && (
            <SponsoredProductCampaignModifiers
              state={state}
              onChange={onChange}
              errors={errors}
              fieldsToValidate={fieldsToValidate}
              review={review}
              bidStrategyOptions={bidStrategyOptions}
              readOnly={!userCanUpdateAds}
            />
          )}
        {channel === "amazon" &&
          state.adType === AD_TYPES.SPONSORED_PRODUCT.value &&
          state.goal !== GOALS.SOV.value &&
          !state.parent &&
          !state.canHaveChildren && (
            <Flex
              wrap="wrap"
              style={{
                marginTop: "8px",
                marginBottom: "0",
              }}
            >
              <div style={{ width: "100%" }}>
                <h3 className={subtitleStyle}>{t(($) => $.advertising["Campaign Budget Split:"])}</h3>
              </div>
              <Flex
                wrap="wrap"
                style={{
                  paddingTop: "8px",
                }}
                className={`${css({ width: "100%", lg: { width: "41.67%" } })} ${inputContainerStyle}`}
              >
                <div style={{ padding: "0 2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                  <label
                    style={{
                      color: "#73706E",
                      fontWeight: "600",
                      paddingBottom: ".7rem",
                    }}
                  >
                    {t(($) => $.advertising["Manual"])}
                  </label>
                  <FormGroupTile
                    type="formcontrol"
                    prefix={"%"}
                    prefixSide={"right"}
                    formControlType={"number"}
                    stateName={"manualCampaignBudget"}
                    handleChildFormElement={(key, value) => {
                      if (value > 100 || value < 0) {
                        return;
                      }

                      onChange({
                        [key]: value,
                        autoCampaignBudget: parseInt(100 - value) ?? 0,
                      });
                    }}
                    placeholder={t(($) => $.advertising["Manual campaign split"])}
                    defaultValue={parseInt(state.manualCampaignBudget)}
                    disabled={!userCanUpdateAds}
                  />
                </div>
                <div style={{ padding: "0 2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                  <label
                    style={{
                      color: "#73706E",
                      fontWeight: "600",
                      paddingBottom: ".7rem",
                    }}
                  >
                    {t(($) => $.advertising["Auto"])}
                  </label>
                  <FormGroupTile
                    type="formcontrol"
                    prefix={"%"}
                    prefixSide={"right"}
                    formControlType={"number"}
                    stateName={"autoCampaignBudget"}
                    handleChildFormElement={(key, value) => {
                      if (value > 100 || value < 0) {
                        return;
                      }

                      onChange({
                        [key]: value,
                        manualCampaignBudget: parseInt(100 - value) ?? 0,
                      });
                    }}
                    placeholder={t(($) => $.advertising["Auto campaign split"])}
                    defaultValue={parseInt(state.autoCampaignBudget)}
                    disabled={!userCanUpdateAds}
                  />
                </div>
              </Flex>
              <div className={css({ width: "100%", lg: { width: "58.33%" } })}>
                <p
                  className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
                  style={{ paddingTop: "2rem" }}
                >
                  {t(($) => $.advertising["Adjust the budget split between manual and auto campaigns."])}
                </p>
              </div>
            </Flex>
          )}
        {!state.parent && !state.canHaveChildren && (
          <Flex
            wrap="wrap"
            style={{
              marginTop: "8px",
              marginBottom: "0",
            }}
          >
            <div style={{ width: "100%" }}>
              <h3 className={subtitleStyle}>{t(($) => $.advertising["Lookback Windows:"])}</h3>
            </div>
            <Flex
              wrap="wrap"
              style={{
                paddingTop: "8px",
              }}
              className={`${css({ width: "100%", lg: { width: "41.67%" } })} ${inputContainerStyle}`}
            >
              <div style={{ padding: "0 2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                <label
                  style={{
                    color: "#73706E",
                    fontWeight: "600",
                    paddingBottom: ".7rem",
                  }}
                >
                  {t(($) => $.advertising["ACOS Lookback Window"])}
                </label>
                <FormGroupTile
                  type="formcontrol"
                  formControlType={"number"}
                  stateName={"acosLookbackWindow"}
                  handleChildFormElement={(key, value) => {
                    onChange({
                      [key]: value,
                    });
                  }}
                  defaultValue={parseInt(state.acosLookbackWindow)}
                  disabled={!userCanUpdateAds}
                />
              </div>
              <div style={{ padding: "0 2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                <label
                  style={{
                    color: "#73706E",
                    fontWeight: "600",
                    paddingBottom: ".7rem",
                  }}
                >
                  {t(($) => $.advertising["CR Lookback Window"])}
                </label>
                <FormGroupTile
                  type="formcontrol"
                  formControlType={"number"}
                  stateName={"crLookbackWindow"}
                  handleChildFormElement={(key, value) => {
                    onChange({
                      [key]: value,
                    });
                  }}
                  defaultValue={parseInt(state.crLookbackWindow)}
                  disabled={!userCanUpdateAds}
                />
              </div>
            </Flex>
            <div className={css({ width: "100%", lg: { width: "58.33%" } })}>
              <p
                className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
                style={{ paddingTop: "2rem" }}
              >
                {t(
                  ($) =>
                    $.advertising[
                      "Adjust the period that ACOS and conversion rate are considered over when optimizing bids"
                    ],
                )}
              </p>
            </div>
          </Flex>
        )}
        {channel === "amazon" &&
          ([AD_TYPES.SPONSORED_PRODUCT_AUTO.value]?.includes(state.adType) ||
            ([AD_TYPES.SPONSORED_PRODUCT.value].includes(state.adType) && state.autoCampaignEnabled)) && (
            <Flex
              wrap="wrap"
              style={{
                marginTop: "8px",
                marginBottom: "0",
              }}
            >
              <div style={{ width: "100%" }}>
                <h3 className={subtitleStyle}>{t(($) => $.advertising["Automatic Keyword Negation Settings:"])}</h3>
              </div>
              <Flex
                wrap="wrap"
                style={{
                  paddingTop: "8px",
                }}
                className={`${css({ width: "100%", lg: { width: "41.67%" } })} ${inputContainerStyle}`}
              >
                <div
                  style={{ padding: "2rem .5rem 0" }}
                  className={`${css({ width: "100%", "2xl": { width: "75%" } })} d-flex align-items-center`}
                >
                  <Checkbox
                    label={t(($) => $.advertising["Auto Negation"])}
                    value={state.autoNegationEnabled}
                    onChange={(v) => {
                      if (v) {
                        onChange({
                          maxAcosNegationThreshold: MAX_ACOS_NEGATION_THESHOLD_DEFAULT,
                          cumulativeNegationThreshold: CUMULATIVE_NEGATION_THESHOLD_DEFAULT,
                          autoNegationEnabled: true,
                        });
                      } else {
                        onChange({
                          maxAcosNegationThreshold: null,
                          cumulativeNegationThreshold: null,
                          autoNegationEnabled: false,
                        });
                      }
                    }}
                    isDisabled={!userCanUpdateAds}
                  />
                </div>
                {state.autoNegationEnabled && (
                  <>
                    <div style={{ padding: "2rem 2rem 0" }}>
                      <label
                        style={{
                          color: "#73706E",
                          fontWeight: "600",
                          paddingBottom: ".7rem",
                        }}
                      >
                        {t(($) => $.advertising["Optimal ACOS Range High Negation Threshold"])}
                      </label>
                      <div style={{ paddingRight: "2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                        <FormGroupTile
                          type="formcontrol"
                          prefix={"%"}
                          prefixSide={"right"}
                          formControlType={"number"}
                          stateName={"maxAcosNegationThreshold"}
                          handleChildFormElement={(key, value) => {
                            if (value < 0) {
                              return;
                            }

                            onChange({
                              [key]: value,
                              maxAcosNegationThreshold: parseInt(value) ?? 0,
                            });
                          }}
                          placeholder={t(($) => $.advertising["Negation threshold"])}
                          defaultValue={parseInt(state.maxAcosNegationThreshold)}
                          disabled={!userCanUpdateAds}
                        />
                      </div>
                    </div>

                    <div style={{ padding: "2rem 2rem 0" }}>
                      <label
                        style={{
                          color: "#73706E",
                          fontWeight: "600",
                          paddingBottom: ".7rem",
                        }}
                      >
                        {t(($) => $.advertising["Cumulative Negation Threshold"])}
                      </label>

                      <div style={{ paddingRight: "2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                        <FormGroupTile
                          type="formcontrol"
                          prefix={"%"}
                          prefixSide={"right"}
                          formControlType={"number"}
                          stateName={"cumulativeNegationThreshold"}
                          handleChildFormElement={(key, value) => {
                            if (value < 0) {
                              return;
                            }

                            onChange({
                              [key]: value,
                              cumulativeNegationThreshold: parseInt(value) ?? 0,
                            });
                          }}
                          placeholder={t(($) => $.advertising["Negation threshold"])}
                          defaultValue={parseInt(state.cumulativeNegationThreshold)}
                          disabled={!userCanUpdateAds}
                        />
                      </div>
                    </div>
                  </>
                )}
              </Flex>
              <div style={{ paddingRight: "5rem" }} className={css({ width: "100%", lg: { width: "58.33%" } })}>
                {state.autoNegationEnabled && (
                  <>
                    <p
                      className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
                      style={{ paddingTop: "85px" }}
                    >
                      {t(
                        ($) =>
                          $.advertising[
                            "Keywords above {percent}% ACOS will be negated after they accumulate sufficient spend."
                          ],
                        {
                          percent:
                            (state.maxAcosNegationThreshold / 100) *
                            (preferences.acos_or_roas === "roas" ?
                              (1 / parseFloat(state.maxACOS)) * 100
                            : state.maxACOS),
                        },
                      )}
                    </p>
                    <p
                      className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
                      style={{ paddingTop: "50px" }}
                    >
                      {t(
                        ($) => $.advertising["If the media plan ACOS is below {percent}% auto negation will not run."],
                        {
                          percent:
                            (state.cumulativeNegationThreshold / 100) *
                            (preferences.acos_or_roas === "roas" ?
                              (1 / parseFloat(state.maxACOS)) * 100
                            : state.maxACOS),
                        },
                      )}
                    </p>
                  </>
                )}
              </div>
            </Flex>
          )}
        {[AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value].includes(state.adType) && (
          <>
            <Flex wrap="wrap">
              <div style={{ width: "100%" }}>
                <h3 className={subtitleStyle}>{t(($) => $.advertising["Amazon Options:"])}</h3>
                <Flex wrap="wrap">
                  <Flex
                    wrap="wrap"
                    style={{
                      paddingTop: "8px",
                    }}
                    className={`${css({ width: "100%", lg: { width: "41.67%" } })} ${inputContainerStyle}`}
                  >
                    <div
                      style={{ padding: "0 .5rem" }}
                      className={`${css({ width: "100%", "2xl": { width: "75%" } })} d-flex align-items-center`}
                    >
                      <Checkbox
                        label={t(($) => $.advertising["Bid Optimization"])}
                        value={state.amazonSBBidOptimization}
                        onChange={(v) => {
                          if (v) {
                            // Clear placement multipliers when checked
                            onChange({
                              amazonSBBidOptimization: !state.amazonSBBidOptimization,
                              homePlacementBidMultiplier: 0,
                              pagePlacementBidMultiplier: 0,
                              otherPlacementBidMultiplier: 0,
                              topOfSearchPlacementBidMultiplier: 0,
                            });
                            return;
                          }
                          onChange({
                            amazonSBBidOptimization: !state.amazonSBBidOptimization,
                          });
                        }}
                        isDisabled={!userCanUpdateAds}
                      />
                    </div>
                  </Flex>
                  <div className={css({ width: "100%", lg: { width: "58.33%" } })}>
                    <p className={css(explanationTextStyle, review && reviewExplanationTextStyle)}>
                      {t(($) => $.advertising["If selected, Amazon will automatically set bid placement adjustments."])}
                    </p>
                  </div>
                </Flex>
              </div>
              {/* Sponsored Brand Campaign Goal Field */}
              {SB_CAMPAIGN_GOAL_SUPPORTED_MARKETPLACES.includes(marketPlace.marketPlace) &&
                // Below hides goal field if product video type select, only applicable for SB and SBV ad types
                // that link to a store landing page
                state.videoAdType !== "video" && (
                  <Flex
                    wrap="wrap"
                    style={{
                      paddingTop: "8px",
                    }}
                    className={`${css({ width: "100%", lg: { width: "41.67%" } })} ${inputContainerStyle}`}
                  >
                    <div style={{ padding: "0 2rem" }}>
                      <label
                        style={{
                          color: "#73706E",
                          fontWeight: "600",
                          paddingBottom: ".7rem",
                        }}
                      >
                        {t(($) => $.advertising["Goal"])}
                      </label>
                      <FormGroupTile
                        type="select"
                        stateName={"sbCampaignGoal"}
                        handleChildFormElement={(key, value) => {
                          onChange({
                            [key]: value.value,
                          });
                        }}
                        disabled={state.id || !userCanUpdateAds}
                        defaultValue={campaignGoalOptions.find((a) => a.value === state.sbCampaignGoal)}
                        options={campaignGoalOptions}
                      />
                    </div>
                  </Flex>
                )}
            </Flex>
            {/* Placement Bid Multipliers */}
            {!state.amazonSBBidOptimization && (
              <Flex
                wrap="wrap"
                style={{
                  marginTop: "8px",
                  marginBottom: "0",
                }}
              >
                <div style={{ width: "100%" }}>
                  <h3 className={subtitleStyle}>{t(($) => $.advertising["Amazon Placement Multipliers:"])}</h3>
                  <p
                    className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
                    style={{ paddingTop: "1rem" }}
                  >
                    {t(
                      ($) =>
                        $.advertising[
                          "Placement bid multipliers give you additional opportunities to optimize your advertising campaigns."
                        ],
                    )}
                  </p>
                </div>
                {[ALGORITHM_OPTIONS.GROWTH_MAX.value, ALGORITHM_OPTIONS.ROAS_BOOST.value].includes(state.algorithm) ?
                  <>
                    <div className={`${css({ width: "100%", lg: { width: "41.67%" } })} ${css({ padding: "small" })}`}>
                      <Checkbox
                        label={t(($) => $.advertising["Algorithmically Manage Placement Multipliers"])}
                        value={state.algoManagedPlacementMultipliers}
                        onChange={(v) => {
                          onChange({
                            algoManagedPlacementMultipliers: v,
                          });
                        }}
                        isDisabled={!userCanUpdateAds}
                      />
                    </div>
                    <div
                      className={`${css({ width: "100%", lg: { width: "58.33%" } })} ${css({ paddingTop: "medium" })}`}
                    >
                      <p className={css(explanationTextStyle, review && reviewExplanationTextStyle)}>
                        {t(
                          ($) =>
                            $.advertising[
                              "Select this option to enable Placement Boost and let Trellis automatically optimize your placement multipliers."
                            ],
                        )}
                      </p>
                    </div>
                  </>
                : null}
                <Flex
                  wrap="wrap"
                  style={{
                    paddingTop: "8px",
                  }}
                  className={`${css({ width: "100%", lg: { width: "41.67%" } })} ${inputContainerStyle}`}
                >
                  <div style={{ padding: "0 2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                    <label
                      style={{
                        color: "#73706E",
                        fontWeight: "600",
                        paddingBottom: ".7rem",
                      }}
                    >
                      {t(($) => $.advertising["Amazon Home Page"])}
                    </label>
                    <FormGroupTile
                      type="formcontrol"
                      formControlType={"number"}
                      prefix={"%"}
                      prefixSide={"right"}
                      stateName={"homePlacementBidMultiplier"}
                      handleChildFormElement={(key, value) => {
                        onChange({ [key]: value });
                      }}
                      disabled={
                        ([ALGORITHM_OPTIONS.GROWTH_MAX.value, ALGORITHM_OPTIONS.ROAS_BOOST.value].includes(
                          state.algorithm,
                        ) &&
                          state.algoManagedPlacementMultipliers) ||
                        !userCanUpdateAds
                      }
                      placeholder={t(($) => $.advertising["Home"])}
                      defaultValue={state.homePlacementBidMultiplier}
                    />
                  </div>
                  <div style={{ padding: "0 2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                    <label
                      style={{
                        color: "#73706E",
                        fontWeight: "600",
                        paddingBottom: ".7rem",
                      }}
                    >
                      {t(($) => $.advertising["Product Detail Page"])}
                    </label>
                    <FormGroupTile
                      type="formcontrol"
                      formControlType={"number"}
                      prefix={"%"}
                      prefixSide={"right"}
                      stateName={"pagePlacementBidMultiplier"}
                      handleChildFormElement={(key, value) => {
                        onChange({
                          [key]: value,
                        });
                      }}
                      placeholder={t(($) => $.advertising["Page"])}
                      disabled={
                        ([ALGORITHM_OPTIONS.GROWTH_MAX.value, ALGORITHM_OPTIONS.ROAS_BOOST.value].includes(
                          state.algorithm,
                        ) &&
                          state.algoManagedPlacementMultipliers) ||
                        !userCanUpdateAds
                      }
                      defaultValue={state.pagePlacementBidMultiplier}
                    />
                  </div>
                  <div style={{ padding: "2rem 2rem 0" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                    <label
                      style={{
                        color: "#73706E",
                        fontWeight: "600",
                        paddingBottom: ".7rem",
                      }}
                    >
                      {t(($) => $.advertising["Other Amazon Pages"])}
                    </label>
                    <FormGroupTile
                      type="formcontrol"
                      formControlType={"number"}
                      prefix={"%"}
                      prefixSide={"right"}
                      stateName={"otherPlacementBidMultiplier"}
                      handleChildFormElement={(key, value) => onChange({ [key]: value })}
                      placeholder={t(($) => $.advertising["Other"])}
                      disabled={
                        ([ALGORITHM_OPTIONS.GROWTH_MAX.value, ALGORITHM_OPTIONS.ROAS_BOOST.value].includes(
                          state.algorithm,
                        ) &&
                          state.algoManagedPlacementMultipliers) ||
                        !userCanUpdateAds
                      }
                      defaultValue={state.otherPlacementBidMultiplier}
                    />
                  </div>
                  <div style={{ padding: "2rem 2rem 0" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                    <label
                      style={{
                        color: "#73706E",
                        fontWeight: "600",
                        paddingBottom: ".7rem",
                      }}
                    >
                      {t(($) => $.advertising["Top of Search"])}
                    </label>
                    <FormGroupTile
                      type="formcontrol"
                      formControlType={"number"}
                      prefix={"%"}
                      prefixSide={"right"}
                      stateName={"topOfSearchPlacementBidMultiplier"}
                      handleChildFormElement={(key, value) => onChange({ [key]: value })}
                      placeholder={t(($) => $.advertising["Other"])}
                      disabled={
                        ([ALGORITHM_OPTIONS.GROWTH_MAX.value, ALGORITHM_OPTIONS.ROAS_BOOST.value].includes(
                          state.algorithm,
                        ) &&
                          state.algoManagedPlacementMultipliers) ||
                        !userCanUpdateAds
                      }
                      defaultValue={state.topOfSearchPlacementBidMultiplier}
                    />
                  </div>
                </Flex>
                <div className={css({ width: "100%", lg: { width: "58.33%" } })}>
                  <p
                    className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
                    style={{ paddingTop: "2rem" }}
                  >
                    {t(
                      ($) =>
                        $.advertising[
                          "This control allows you to set bid multipliers starting at 0% for home, page, and other, depending on where you want your listings to most often appear."
                        ],
                    )}
                  </p>
                  <PlacementModifierWarning state={state} />
                </div>
                <div style={{ padding: "0 2rem" }}>
                  {errors.homePlacementBidMultiplier && fieldsToValidate?.includes("homePlacementBidMultiplier") ?
                    <small className="text-danger" style={{ paddingLeft: ".8rem", paddingTop: "1rem" }}>
                      {errors.homePlacementBidMultiplier}
                    </small>
                  : errors.pagePlacementBidMultiplier && fieldsToValidate?.includes("pagePlacementBidMultiplier") ?
                    <small className="text-danger" style={{ paddingLeft: ".8rem", paddingTop: "1rem" }}>
                      {errors.pagePlacementBidMultiplier}
                    </small>
                  : errors.otherPlacementBidMultiplier &&
                    fieldsToValidate?.includes("otherPlacementBidMultiplier") && (
                      <small className="text-danger" style={{ paddingLeft: ".8rem", paddingTop: "1rem" }}>
                        {errors.otherPlacementBidMultiplier}
                      </small>
                    )
                  }
                </div>
              </Flex>
            )}
            {/* Segment Bid Multipliers */}
            <div style={{ width: "100%" }}>
              <h3 className={subtitleStyle}>{t(($) => $.advertising["Amazon Segment Multipliers:"])}</h3>
              <p
                className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
                style={{ paddingTop: "1rem" }}
              >
                {t(
                  ($) =>
                    $.advertising[
                      "Segment bid multipliers give you additional opportunities to optimize your advertising campaigns."
                    ],
                )}
              </p>
            </div>

            <Flex wrap="wrap">
              <Flex
                wrap="wrap"
                style={{
                  paddingTop: "8px",
                }}
                className={`${css({ width: "100%", lg: { width: "41.67%" } })} ${inputContainerStyle}`}
              >
                <div style={{ padding: "0 2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                  <label
                    style={{
                      color: "#73706E",
                      fontWeight: "600",
                      paddingBottom: ".7rem",
                    }}
                  >
                    {t(($) => $.advertising["New to Brand"])}
                  </label>
                  <FormGroupTile
                    type="formcontrol"
                    formControlType={"number"}
                    prefix={"%"}
                    prefixSide={"right"}
                    stateName={"shopperSegmentBidMultiplier"}
                    handleChildFormElement={(key, value) => {
                      onChange({ [key]: value });
                    }}
                    placeholder={t(($) => $.advertising["Home"])}
                    defaultValue={state.shopperSegmentBidMultiplier}
                    disabled={!userCanUpdateAds}
                  />
                </div>
              </Flex>
              <div className={css({ width: "100%", lg: { width: "58.33%" } })}>
                <p
                  className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
                  style={{ paddingTop: "2rem" }}
                >
                  {t(
                    ($) =>
                      $.advertising[
                        "The shopper segment multiplier gives you the opportunity to target new-to-brand customers. This multiplier is multiplicative with the above placement multipliers if selected."
                      ],
                  )}
                </p>
              </div>
              <div style={{ padding: "0 2rem" }}>
                {errors.shopperSegmentBidMultiplier && fieldsToValidate?.includes("shopperSegmentBidMultiplier") && (
                  <small className="text-danger" style={{ paddingLeft: ".8rem" }}>
                    {errors.shopperSegmentBidMultiplier}
                  </small>
                )}
              </div>
            </Flex>
            <Flex wrap="wrap">
              <div className={css({ width: "100%", lg: { width: "41.67%" } })}>
                <AudienceMultiplierField
                  state={state}
                  onChange={onChange}
                  errors={errors}
                  fieldsToValidate={fieldsToValidate}
                  disableFields={!userCanUpdateAds}
                />
              </div>
              <div className={css({ width: "100%", lg: { width: "58.33%" } })}>
                <p
                  className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
                  style={{ paddingTop: "2rem" }}
                >
                  {t(($) => $.advertising["Apply a bid multiplier when a shopper matches a target audience."])}
                </p>
                <p
                  className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
                  style={{ paddingTop: "1rem" }}
                >
                  {t(
                    ($) =>
                      $.advertising[
                        "Not seeing your AMC Audience? AMC Audiences can take up to 48 hours to be available in advertising."
                      ],
                  )}
                </p>
              </div>
            </Flex>
            <Flex wrap="wrap">
              <div className={css({ width: "100%", lg: { width: "41.67%" } })}>
                <CostControlField
                  state={state}
                  onChange={onChange}
                  errors={errors}
                  fieldsToValidate={fieldsToValidate}
                  disableFields={!userCanUpdateAds}
                  marketplace={marketPlace?.marketPlace}
                />
              </div>
            </Flex>
          </>
        )}
        {state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value && (
          <Flex wrap="wrap">
            <div className={css({ width: "100%", lg: { width: "41.67%" } })}>
              <CostControlField
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                disableFields={!userCanUpdateAds}
                marketplace={marketPlace?.marketPlace}
                showRecommendation={false}
              />
            </div>
          </Flex>
        )}
        {/*
              Walmart Specific Targeting Options
              Note that all copy below comes directly from the Walmart Advertising API Docs (not marketing) - https://advertisinghelp.walmart.com/s/guides?article=000008409
          */}
        {/* Placement Bid Multipliers */}
        {channel === "walmart" && (
          <>
            {![AD_TYPES.SPONSORED_BRAND_WALMART.value, AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(
              state.adType,
            ) && (
              <Flex
                wrap="wrap"
                style={{
                  marginTop: "8px",
                  marginBottom: "0",
                }}
              >
                <div style={{ width: "100%" }}>
                  <h3 className={subtitleStyle}>{t(($) => $.advertising["Walmart Bid Multipliers:"])}</h3>
                  <p
                    className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
                    style={{ paddingTop: "1rem" }}
                  >
                    {t(
                      ($) =>
                        $.advertising[
                          "Bid multipliers give you additional opportunities to optimize your advertising campaigns."
                        ],
                    )}
                  </p>
                </div>
                <div style={{ width: "100%" }}>
                  <h3 className={subtitleStyle}>{t(($) => $.advertising["Placement Multipliers:"])}</h3>
                </div>
                <Flex
                  wrap="wrap"
                  style={{
                    paddingTop: "8px",
                  }}
                  className={`${css({ width: "100%", lg: { width: "41.67%" } })} ${inputContainerStyle}`}
                >
                  <div style={{ padding: "0 2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                    <label
                      style={{
                        color: "#73706E",
                        fontWeight: "600",
                        paddingBottom: ".7rem",
                      }}
                    >
                      {t(($) => $.advertising["Buy Box"])}
                    </label>
                    <FormGroupTile
                      type="formcontrol"
                      formControlType={"number"}
                      prefix={"%"}
                      prefixSide={"right"}
                      stateName={"buyBoxPlacementBidMultiplier"}
                      handleChildFormElement={(key, value) => onChange({ [key]: value })}
                      placeholder={t(($) => $.advertising["Buy Box"])}
                      defaultValue={state.buyBoxPlacementBidMultiplier}
                      disabled={!userCanUpdateAds}
                    />
                  </div>
                  <div style={{ padding: "0 2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                    <label
                      style={{
                        color: "#73706E",
                        fontWeight: "600",
                        paddingBottom: ".7rem",
                      }}
                    >
                      {t(($) => $.advertising["Search In-Grid"])}
                    </label>
                    <FormGroupTile
                      type="formcontrol"
                      formControlType={"number"}
                      prefix={"%"}
                      prefixSide={"right"}
                      stateName={"searchInGridPlacementBidMultiplier"}
                      handleChildFormElement={(key, value) => onChange({ [key]: value })}
                      placeholder={t(($) => $.advertising["Search In-Grid"])}
                      defaultValue={state.searchInGridPlacementBidMultiplier}
                      disabled={!userCanUpdateAds}
                    />
                  </div>
                  <div style={{ padding: "2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                    <label
                      style={{
                        color: "#73706E",
                        fontWeight: "600",
                        paddingBottom: ".7rem",
                      }}
                    >
                      {t(($) => $.advertising["Stock Up"])}
                    </label>
                    <FormGroupTile
                      type="formcontrol"
                      formControlType={"number"}
                      prefix={"%"}
                      prefixSide={"right"}
                      stateName={"stockUpPlacementBidMultiplier"}
                      handleChildFormElement={(key, value) => onChange({ [key]: value })}
                      placeholder={t(($) => $.advertising["Stock Up"])}
                      defaultValue={state.stockUpPlacementBidMultiplier}
                      disabled={!userCanUpdateAds}
                    />
                  </div>
                  <div style={{ padding: "2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                    <label
                      style={{
                        color: "#73706E",
                        fontWeight: "600",
                        paddingBottom: ".7rem",
                      }}
                    >
                      {t(($) => $.advertising["Home Page"])}
                    </label>
                    <FormGroupTile
                      type="formcontrol"
                      formControlType={"number"}
                      prefix={"%"}
                      prefixSide={"right"}
                      stateName={"homePagePlacementBidMultiplier"}
                      handleChildFormElement={(key, value) => onChange({ [key]: value })}
                      placeholder={t(($) => $.advertising["Home Page"])}
                      defaultValue={state.homePagePlacementBidMultiplier}
                      disabled={!userCanUpdateAds}
                    />
                  </div>
                </Flex>
                <div className={css({ width: "100%", lg: { width: "58.33%" } })}>
                  <p
                    className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
                    style={{ paddingTop: "2rem" }}
                  >
                    {t(
                      ($) =>
                        $.advertising[
                          "This control allows you to set bid modifiers starting at 0% for various placements, depending on your campaign type."
                        ],
                    )}
                  </p>
                </div>
              </Flex>
            )}

            {/* Platform Multipliers */}
            {![AD_TYPES.SPONSORED_BRAND_WALMART.value, AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(
              state.adType,
            ) && (
              <Flex
                wrap="wrap"
                style={{
                  marginTop: "8px",
                  marginBottom: "0",
                }}
              >
                <div style={{ width: "100%" }}>
                  <h3 className={subtitleStyle}>{t(($) => $.advertising["Platform Multipliers:"])}</h3>
                </div>
                <Flex
                  wrap="wrap"
                  style={{
                    paddingTop: "8px",
                  }}
                  className={`${css({ width: "100%", lg: { width: "41.67%" } })} ${inputContainerStyle}`}
                >
                  <div style={{ padding: "0 2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                    <label
                      style={{
                        color: "#73706E",
                        fontWeight: "600",
                        paddingBottom: ".7rem",
                      }}
                    >
                      {t(($) => $.advertising["Desktop"])}
                    </label>
                    <FormGroupTile
                      type="formcontrol"
                      formControlType={"number"}
                      prefix={"%"}
                      prefixSide={"right"}
                      stateName={"desktopPlatformBidMultiplier"}
                      handleChildFormElement={(key, value) => onChange({ [key]: value })}
                      placeholder={t(($) => $.advertising["Desktop"])}
                      defaultValue={state.desktopPlatformBidMultiplier}
                      disabled={!userCanUpdateAds}
                    />
                  </div>
                  <div style={{ padding: "0 2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                    <label
                      style={{
                        color: "#73706E",
                        fontWeight: "600",
                        paddingBottom: ".7rem",
                      }}
                    >
                      {t(($) => $.advertising["Mobile"])}
                    </label>
                    <FormGroupTile
                      type="formcontrol"
                      formControlType={"number"}
                      prefix={"%"}
                      prefixSide={"right"}
                      stateName={"mobilePlatformBidMultiplier"}
                      handleChildFormElement={(key, value) => onChange({ [key]: value })}
                      placeholder={t(($) => $.advertising["Mobile"])}
                      defaultValue={state.mobilePlatformBidMultiplier}
                      disabled={!userCanUpdateAds}
                    />
                  </div>
                  <div style={{ padding: "2rem" }} className={css({ width: "100%", "2xl": { width: "50%" } })}>
                    <label
                      style={{
                        color: "#73706E",
                        fontWeight: "600",
                        paddingBottom: ".7rem",
                      }}
                    >
                      {t(($) => $.advertising["App"])}
                    </label>
                    <FormGroupTile
                      type="formcontrol"
                      formControlType={"number"}
                      prefix={"%"}
                      prefixSide={"right"}
                      stateName={"appPlatformBidMultiplier"}
                      handleChildFormElement={(key, value) => onChange({ [key]: value })}
                      placeholder={t(($) => $.advertising["App"])}
                      defaultValue={state.appPlatformBidMultiplier}
                      disabled={!userCanUpdateAds}
                    />
                  </div>
                </Flex>
                <div className={css({ width: "100%", lg: { width: "58.33%" } })}>
                  <p
                    className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
                    style={{ paddingTop: "2rem" }}
                  >
                    {t(
                      ($) =>
                        $.advertising[
                          "This control allows you to set bid multipliers starting at 0% for desktop, app and mWeb (mobile web) platforms."
                        ],
                    )}
                  </p>
                </div>
              </Flex>
            )}
            {![
              AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value,
              AD_TYPES.SPONSORED_BRAND_WALMART.value,
              AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value,
            ].includes(state.adType) && (
              <Flex
                wrap="wrap"
                style={{
                  marginTop: "8px",
                  marginBottom: "0",
                }}
              >
                <div style={{ width: "100%" }}>
                  <h3 className={subtitleStyle}>{t(($) => $.advertising["Carousel Placement Toggles:"])}</h3>
                </div>
                <Flex
                  wrap="wrap"
                  style={{
                    paddingTop: "8px",
                  }}
                  className={`${css({ width: "100%", lg: { width: "41.67%" } })} ${inputContainerStyle}`}
                >
                  <div style={{ padding: "0 1rem" }} className={css({ width: "100%", "2xl": { width: "33.33%" } })}>
                    <label
                      style={{
                        color: "#73706E",
                        fontWeight: "600",
                        paddingBottom: ".7rem",
                        display: "flex",
                      }}
                    >
                      {t(($) => $.advertising["Search"])}
                      <HoverInfo iconCss={{ color: "grey", display: "inline", paddingStart: "small" }}>
                        {t(($) => $.advertising["Appears on search and browse pages."])}
                      </HoverInfo>
                    </label>
                    <Switch
                      isSelected={state.searchCarouselCampaignPlacement}
                      onChange={() => {
                        onChange({
                          searchCarouselCampaignPlacement: !state.searchCarouselCampaignPlacement,
                        });
                      }}
                    />
                  </div>
                  <div style={{ padding: "0 1rem" }} className={css({ width: "100%", "2xl": { width: "33.33%" } })}>
                    <label
                      style={{
                        color: "#73706E",
                        fontWeight: "600",
                        paddingBottom: ".7rem",
                        display: "flex",
                      }}
                    >
                      {t(($) => $.advertising["Item"])}
                      <HoverInfo iconCss={{ color: "grey", display: "inline", paddingStart: "small" }}>
                        {t(($) => $.advertising["Appears on relevant item pages."])}
                      </HoverInfo>
                    </label>
                    <Switch
                      isSelected={state.itemCarouselCampaignPlacement}
                      isDisabled={!userCanUpdateAds}
                      onChange={() => {
                        onChange({
                          itemCarouselCampaignPlacement: !state.itemCarouselCampaignPlacement,
                        });
                      }}
                    />
                  </div>
                  <div style={{ padding: "0 1rem" }} className={css({ width: "100%", "2xl": { width: "33.33%" } })}>
                    <label
                      style={{
                        color: "#73706E",
                        fontWeight: "600",
                        paddingBottom: ".7rem",
                        display: "flex",
                      }}
                    >
                      {t(($) => $.advertising["Buy Box"])}
                      <HoverInfo iconCss={{ color: "grey", display: "inline", paddingStart: "small" }}>
                        {t(($) => $.advertising['Appears on relevant item pages below the "add to cart" box.'])}
                      </HoverInfo>
                    </label>
                    <Switch
                      isSelected={state.buyBoxCarouselCampaignPlacement}
                      isDisabled={!userCanUpdateAds}
                      onChange={() => {
                        onChange({
                          buyBoxCarouselCampaignPlacement: !state.buyBoxCarouselCampaignPlacement,
                        });
                      }}
                    />
                  </div>
                </Flex>
                <div className={css({ width: "100%", lg: { width: "58.33%" } })}>
                  <p
                    className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
                    style={{ paddingTop: "2rem" }}
                  >
                    {t(
                      ($) =>
                        $.advertising[
                          "Specify where you want your listings to show up using these placement options. By default, the Search In-grid placement is toggled on and cannot be toggled off."
                        ],
                    )}
                  </p>
                </div>
              </Flex>
            )}
            {/* Walmart Suggested Keywords */}
            {![
              AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value,
              AD_TYPES.SPONSORED_BRAND_WALMART.value,
              AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value,
            ].includes(state.adType) && (
              <Flex
                wrap="wrap"
                style={{
                  marginTop: "8px",
                  marginBottom: "0",
                }}
              >
                <div style={{ width: "100%" }}>
                  <h3 className={subtitleStyle}>{t(($) => $.advertising["Walmart Suggested Keywords"])}</h3>
                </div>
                <Flex
                  wrap="wrap"
                  style={{
                    paddingTop: "8px",
                  }}
                  className={`${css({ width: "100%", lg: { width: "41.67%" } })} ${inputContainerStyle}`}
                >
                  <div className={checkboxContainerStyle}>
                    <Checkbox
                      label={t(($) => $.advertising["Use Walmart Suggested Keywords"])}
                      value={state.walmartSuggestedKeywordsEnabled}
                      onChange={() => {
                        onChange({
                          walmartSuggestedKeywordsEnabled: !state.walmartSuggestedKeywordsEnabled,
                        });
                      }}
                      isDisabled={!userCanUpdateAds}
                    />
                  </div>
                </Flex>
                <div className={css({ width: "100%", lg: { width: "58.33%" } })}>
                  <p
                    className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
                    style={{ paddingTop: "2rem" }}
                  >
                    {t(
                      ($) =>
                        $.advertising[
                          "Allow Walmart to automatically identify new keyword opportunities that have the potential to help increase product visibility and boost sales."
                        ],
                    )}
                  </p>
                </div>
              </Flex>
            )}
          </>
        )}
        {/* end of Walmart-specific fields */}
        {state.adType === AD_TYPES.SPONSORED_PRODUCT.value && state.hasParent && (
          <>
            {/* Negative Match Types */}
            <Flex wrap="wrap">
              <div style={{ width: "100%" }}>
                <h3 className={subtitleStyle}>{t(($) => $.advertising["Negative Match Type:"])}</h3>
              </div>
              <Flex wrap="wrap" className={`${css({ width: "100%", lg: { width: "41.67%" } })} ${inputContainerStyle}`}>
                <div className={`${css({ width: "100%", "2xl": { width: "33.33%" } })} ${checkboxContainerStyle}`}>
                  <Checkbox
                    label={t(($) => $.advertising["Phrase"])}
                    value={state.negativePhraseMatchEnabled}
                    isDisabled={state.initialNegativePhraseMatchEnabled || !userCanUpdateAds}
                    onChange={() => {
                      onChange({
                        negativePhraseMatchEnabled: !state.negativePhraseMatchEnabled,
                      });
                    }}
                  />
                </div>
                <div className={`${css({ width: "100%", "2xl": { width: "33.33%" } })} ${checkboxContainerStyle}`}>
                  <Checkbox
                    label={t(($) => $.advertising["Exact"])}
                    value={state.negativeExactMatchEnabled}
                    isDisabled={state.initialExactMatchEnabled || !userCanUpdateAds}
                    onChange={() => {
                      onChange({
                        negativeExactMatchEnabled: !state.negativeExactMatchEnabled,
                      });
                    }}
                  />
                </div>
              </Flex>
              <div className={css({ width: "100%", lg: { width: "58.33%" } })}>
                <p
                  className={css(explanationTextStyle, review && reviewExplanationTextStyle)}
                  style={{ paddingTop: "2rem" }}
                >
                  {t(
                    ($) =>
                      $.advertising[
                        "Allows you to enable/disable automated discovery of keywords as well as harvest keywords that match and broad or phrase terms."
                      ],
                  )}
                </p>
              </div>
            </Flex>
          </>
        )}{" "}
        {((!state.hasParent && channel === "amazon") ||
          (channel === "walmart" && WALMART_NEGATIVE_KEYWORD_AD_TYPES.includes(state.adType))) && (
          <div style={{ width: "100%" }} className="mt-5">
            <NegativeKeywordsField
              state={state}
              onChange={onChange}
              errors={errors}
              channel={channel}
              isDisabled={!userCanUpdateAds}
            />
          </div>
        )}
        {!state.hasParent && channel === "amazon" && (
          <div style={{ width: "100%" }}>
            <NegativeAsinsField
              state={state}
              onChange={onChange}
              errors={errors}
              channel={channel}
              isDisabled={!userCanUpdateAds}
            />
          </div>
        )}
        {!state.canHaveChildren &&
          (state.keywordHarvestingEnabled || state.asinHarvestingEnabled) &&
          ![AD_TYPES.SPONSORED_PRODUCT_AUTO.value, AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value].includes(
            state.adType,
          ) && <HarvestingConfiguration onChange={onChange} state={state} readOnly={!userCanUpdateAds} />}
      </Flex>
    );
  };

  return (review && !state.id) || state.canHaveChildren ?
      null
    : <div className={menuContainer}>
        {showAccordion ?
          <AccordionComponent
            title={t(($) => $.advertising["Advanced Options"])}
            isExpanded={open}
            onExpandedChange={setOpen}
            marginTop="0"
          >
            {renderAdvancedSettings()}
          </AccordionComponent>
        : renderAdvancedSettings()}
      </div>;
};

export default AdvancedTargetingOptions;
